import { computed, Injectable, signal } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, timeout } from 'rxjs';
import { ApiService } from './api-data.service';
import { URLS } from '../_config/api.config';

// Ported from customer-website's SEOConfigurationService so page titles,
// meta description, OG tags and the footer copyright come from the backend
// at runtime instead of being hardcoded, same as customer-website.
interface SEOConfigItem {
	id: number;
	config_key: string;
	description: string;
	limit_value: string;
	config_value?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class SEOConfigurationService {
	private _configData = signal<SEOConfigItem[]>([]);
	private _loadObs$: Observable<void> | null = null;

	readonly socialLinks = computed(() => this._configData()
		.filter(item => /_link$/.test(item.config_key) && item.config_value !== false)
		.map(item => {
			const network = item.config_key.replace(/_link$/, '');
			const labels: Record<string, string> = { linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok', whatsapp: 'WhatsApp', twitter: 'Twitter / X', x: 'X' };
			const icons: Record<string, string> = { facebook: 'facebook', instagram: 'instagram', linkedin: 'linkedin' };
			return {
				key: item.config_key,
				label: labels[network] || network.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
				url: (item.limit_value || '').trim(),
				icon: icons[network] ? `assets/icon/${icons[network]}.svg` : null,
			};
		})
		.filter(link => {
			try {
				const url = new URL(link.url);
				return url.protocol === 'https:' || url.protocol === 'http:';
			} catch {
				return false;
			}
		}));

	private _configMap = computed(() => {
		return this._configData().reduce((acc, item) => {
			acc[item.config_key] = item.limit_value;
			return acc;
		}, {} as Record<string, string>);
	});

	private _activeMap = computed(() => {
		return this._configData().reduce((acc, item) => {
			acc[item.config_key] = item.config_value;
			return acc;
		}, {} as Record<string, boolean>);
	});

	constructor(private _apiService: ApiService) { }

	public ensureConfigurationLoaded(): Observable<void> {
		if (this._loadObs$) {
			return this._loadObs$;
		}

		this._loadObs$ = this._apiService.get(URLS.config, { config_type: 'seo' }).pipe(
			timeout(2500),
			map((response: any) => {
				this._configData.set(response?.data || []);
			}),
			catchError(() => {
				this._configData.set([]);
				return of(undefined);
			}),
			shareReplay(1),
		);

		return this._loadObs$;
	}

	get(key: string): string {
		return this._configMap()[key] || '';
	}

	getActive(key: string): boolean {
		return this._activeMap()[key] || false;
	}
}
